import { useEffect, useRef, useState } from 'react'
import { BottomSheet } from './BottomSheet.jsx'
import { searchCurrencies } from '../api.js'

// Picker valuta con ricerca su tutte le valute (endpoint condiviso
// /currencies/search) — adattato dal CurrencySelect di gunsi-tally.
// `recentCodes`: valute da mostrare in cima a query vuota (es. quelle già
// usate nel gruppo).

const DEBOUNCE_MS = 250
const cache = new Map() // q (lowercased) → array di currency docs

async function fetchCurrencies(q) {
  const key = q.trim().toLowerCase()
  if (cache.has(key)) return cache.get(key)
  const docs = await searchCurrencies(key, 50)
  cache.set(key, docs ?? [])
  return docs ?? []
}

export function CurrencySelect({ value, onChange, recentCodes = [], title, searchPlaceholder }) {
  const [open, setOpen]       = useState(false)
  const [query, setQuery]     = useState('')
  const [items, setItems]     = useState([])
  const [loading, setLoading] = useState(false)
  const [recentDocs, setRecentDocs] = useState([])
  const inputRef   = useRef(null)
  const debounceId = useRef(null)
  const queryReq   = useRef(0)

  // Lista iniziale + docs delle recenti quando si apre
  useEffect(() => {
    if (!open) return
    setQuery('')
    setLoading(true)
    const reqId = ++queryReq.current
    fetchCurrencies('').then(docs => {
      if (queryReq.current !== reqId) return
      setItems(docs)
      setLoading(false)
    })
    const codes = [...new Set(recentCodes)].slice(0, 5)
    Promise.all(codes.map(code =>
      fetchCurrencies(code.toLowerCase()).then(docs => docs.find(d => d.code === code))
    )).then(found => setRecentDocs(found.filter(Boolean)))
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [open])

  // Search debounced
  useEffect(() => {
    if (!open) return
    clearTimeout(debounceId.current)
    debounceId.current = setTimeout(async () => {
      setLoading(true)
      const reqId = ++queryReq.current
      const docs = await fetchCurrencies(query)
      if (queryReq.current !== reqId) return
      setItems(docs)
      setLoading(false)
    }, DEBOUNCE_MS)
    return () => clearTimeout(debounceId.current)
  }, [query, open])

  function pick(code) {
    onChange(code)
    setOpen(false)
  }

  const row = (cur, keyPrefix = '') => (
    <button
      key={keyPrefix + cur.code}
      type="button"
      className={`cs-row${value === cur.code ? ' selected' : ''}`}
      onClick={() => pick(cur.code)}
    >
      <span className="cs-row-code">{cur.code}</span>
      <span className="cs-row-sym">{cur.symbol_native || cur.symbol}</span>
      <span className="cs-row-name">{cur.name}</span>
    </button>
  )

  return (
    <>
      <button type="button" className="cs-trigger" onClick={() => setOpen(true)}>
        {value || '—'} <span className="cs-trigger-caret" aria-hidden="true">▾</span>
      </button>

      <BottomSheet open={open} onClose={() => setOpen(false)} className="bsheet-select">
        <div className="select-sheet cs-sheet">
          {title && <header className="select-sheet-head">{title}</header>}
          <input
            ref={inputRef}
            type="search"
            className="cs-search"
            placeholder={searchPlaceholder}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <div className="cs-list">
            {loading && items.length === 0 && (
              <div className="cs-empty"><div className="loading-spinner" /></div>
            )}
            {query.trim() === '' && recentDocs.length > 0 && (
              <>
                {recentDocs.map(cur => row(cur, 'recent-'))}
                <div className="cs-separator" aria-hidden="true" />
              </>
            )}
            {items
              .filter(cur => query.trim() !== '' || !recentDocs.some(r => r.code === cur.code))
              .map(cur => row(cur))}
          </div>
        </div>
      </BottomSheet>
    </>
  )
}
